import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { CreditCard, LocalAtm, OpenInNew, Storefront } from '@mui/icons-material';
import apiService from '../services/apiService';
import { colors } from '../theme/theme';
import { deriveRevenue, sumRevenue, DerivedRevenue } from '../utils/revenue';

interface Location {
  _id: string;
  studioName: string;
  branchName?: string | null;
  branchCode?: string | null;
  city?: string | null;
}

interface Row {
  location: Location;
  revenue: DerivedRevenue;
  bookings: number;
  failed?: boolean;
}

const MONTHS = [
  'All year', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/**
 * IST month boundaries expressed in UTC.
 *
 * The studio dashboard uses the same conversion. A UTC month boundary is 5½
 * hours out, which silently moves every transaction in that window into the
 * neighbouring month — visible as two dashboards disagreeing by a few
 * thousand rupees at the start of a month.
 */
const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
function istRange(year: number, month: number) {
  const startIST = Date.UTC(year, month === 0 ? 0 : month - 1, 1, 0, 0, 0, 0);
  const endIST =
    month === 0
      ? Date.UTC(year + 1, 0, 1, 0, 0, 0, 0) - 1
      : Date.UTC(year, month, 1, 0, 0, 0, 0) - 1;
  return {
    startDate: new Date(startIST - IST_OFFSET_MS).toISOString(),
    endDate: new Date(endIST - IST_OFFSET_MS).toISOString(),
  };
}

const inr = (n: number) =>
  `₹${Math.round(n).toLocaleString('en-IN')}`;
const inrExact = (n: number) =>
  `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  THE BRAND OWNER'S DASHBOARD
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Shown only while "All locations" is selected. Choosing one location drops
 * straight back to the ordinary studio dashboard.
 *
 * ── WHY IT CALLS THE SAME ENDPOINT ONCE PER LOCATION ──────────────────────
 *
 * The previous org view computed its own revenue, and disagreed with the
 * studio dashboards it was meant to summarise — one said ₹1,87,251 for
 * Koramangala, the studio's own dashboard said ₹1,25,702 for the same month.
 * Two numbers, both presented as fact, and no way for an owner to know which
 * to trust.
 *
 * So this does not compute anything. It calls the endpoint each studio
 * dashboard already calls, once per location, runs the SAME derivation
 * (`deriveRevenue`), and adds them up. The brand total is the sum of what each
 * location's own dashboard shows — by construction, not by coincidence. If
 * that derivation is ever corrected, this follows without anyone remembering.
 *
 * The cost is N requests for N locations. Deliberate: correctness that cannot
 * drift is worth more than one round trip, and a brand has a handful of
 * locations, not thousands. They are issued in parallel.
 */
const BrandDashboard: React.FC<{ brandName?: string | null }> = ({ brandName }) => {
  const navigate = useNavigate();
  const now = new Date(Date.now() + IST_OFFSET_MS);

  const [year, setYear] = useState(now.getUTCFullYear());
  const [month, setMonth] = useState(now.getUTCMonth() + 1);
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const mine: any = await apiService.getMyBrand();
      const locations: Location[] = mine?.studios ?? [];
      if (!locations.length) {
        setRows([]);
        return;
      }

      const range = istRange(year, month);

      // In parallel — a brand of six should not wait six round trips.
      const results = await Promise.all(
        locations.map(async (loc) => {
          try {
            // Counted by the SERVER (`count` is the full total for the
            // filter), not from a page of rows: a 1,000-row page silently
            // under-counted any busy location. Cancelled bookings are not
            // bookings, so it is total minus cancelled — two one-row requests.
            const [stats, all, cancelled]: any[] = await Promise.all([
              apiService.getStudioTransactionStatsFor(loc._id, range),
              apiService.getBookingsFor(loc._id, { ...range, pageSize: 1 }),
              apiService.getBookingsFor(loc._id, { ...range, pageSize: 1, status: 'CANCELLED' }),
            ]);
            return {
              location: loc,
              revenue: deriveRevenue(stats),
              bookings: Math.max(0, (all?.count ?? 0) - (cancelled?.count ?? 0)),
            } as Row;
          } catch {
            // One location failing must not blank the whole brand. It is
            // shown as unavailable and excluded from the totals, rather than
            // counted as zero — a silent zero reads as "no revenue".
            return {
              location: loc,
              revenue: deriveRevenue(null),
              bookings: 0,
              failed: true,
            } as Row;
          }
        }),
      );
      setRows(results);
    } catch (e: any) {
      setError(e.message || 'Could not load the brand dashboard');
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    load();
  }, [load]);

  const ok = useMemo(() => rows.filter((r) => !r.failed), [rows]);
  const totals = useMemo(() => sumRevenue(ok.map((r) => r.revenue)), [ok]);
  const totalBookings = useMemo(() => ok.reduce((n, r) => n + r.bookings, 0), [ok]);
  const failed = rows.filter((r) => r.failed);

  const label = (l: Location) => l.branchName || l.studioName;
  const periodLabel = month === 0 ? String(year) : `${MONTHS[month]} ${year}`;
  const years = Array.from({ length: 4 }, (_, i) => now.getUTCFullYear() - i);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', md: 'flex-start' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4" fontWeight={700} sx={{ letterSpacing: '-0.5px' }}>
            {brandName || 'Your brand'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {rows.length} location{rows.length === 1 ? '' : 's'} · {periodLabel}
            {' · '}
            <Box component="span" sx={{ color: 'text.disabled' }}>
              same figures each location&rsquo;s own dashboard shows
            </Box>
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5}>
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel>Year</InputLabel>
            <Select label="Year" value={year} onChange={(e) => setYear(Number(e.target.value))}>
              {years.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Month</InputLabel>
            <Select label="Month" value={month} onChange={(e) => setMonth(Number(e.target.value))}>
              {MONTHS.map((m, i) => <MenuItem key={m} value={i}>{m}</MenuItem>)}
            </Select>
          </FormControl>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {!!failed.length && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Could not load {failed.map((f) => label(f.location)).join(', ')}. Those
          locations are excluded from the totals below rather than counted as zero.
        </Alert>
      )}
      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      {/* ── Brand totals ────────────────────────────────────────────────── */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, flex: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Revenue ({periodLabel})
          </Typography>
          <Typography variant="h4" fontWeight={700} sx={{ mt: 0.5, letterSpacing: '-1px' }}>
            {inrExact(totals.netRevenue)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            + Tax {inrExact(totals.tax)} = <b>{inr(totals.total)}</b> total
          </Typography>

          <Divider sx={{ my: 1.5 }} />
          <Stack spacing={1}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <CreditCard sx={{ fontSize: 18, color: colors.info }} />
              <Typography variant="body2" sx={{ flex: 1 }}>Razorpay</Typography>
              <Typography variant="body2" fontWeight={600}>{inr(totals.razorpayGross)}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ width: 64, textAlign: 'right' }}>
                {totals.razorpayCount} txns
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <LocalAtm sx={{ fontSize: 18, color: colors.success }} />
              <Typography variant="body2" sx={{ flex: 1 }}>Cash / manual</Typography>
              <Typography variant="body2" fontWeight={600}>{inr(totals.cashGross)}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ width: 64, textAlign: 'right' }}>
                {totals.cashCount} txns
              </Typography>
            </Stack>
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, minWidth: 200 }}>
          <Typography variant="body2" color="text.secondary">
            Bookings ({periodLabel})
          </Typography>
          <Typography variant="h4" fontWeight={700} sx={{ mt: 0.5 }}>
            {totalBookings}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            cancelled excluded
          </Typography>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, minWidth: 200 }}>
          <Typography variant="body2" color="text.secondary">Locations</Typography>
          <Typography variant="h4" fontWeight={700} sx={{ mt: 0.5 }}>
            {rows.length}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {ok.length === rows.length ? 'all reporting' : `${ok.length} reporting`}
          </Typography>
        </Paper>
      </Stack>

      {/* ── Per location ────────────────────────────────────────────────── */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ p: 2.5, pb: 1.5 }}>
          <Typography variant="h6" fontWeight={700}>By location</Typography>
          <Typography variant="body2" color="text.secondary">
            Each row is that location&rsquo;s own dashboard. They add up to the totals above.
          </Typography>
        </Box>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: colors.backgroundTertiary }}>
                <TableCell sx={{ fontWeight: 700 }}>Location</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Bookings</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Razorpay</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Cash</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Tax</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Total</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Share</TableCell>
                <TableCell width={44} />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => {
                const share = totals.total > 0 ? (r.revenue.total / totals.total) * 100 : 0;
                return (
                  <TableRow key={r.location._id} hover sx={{ opacity: r.failed ? 0.5 : 1 }}>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {label(r.location)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {r.location.studioName}
                          </Typography>
                        </Box>
                        {r.location.branchCode && (
                          <Chip size="small" variant="outlined" label={r.location.branchCode} />
                        )}
                        {r.failed && <Chip size="small" color="warning" label="unavailable" />}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">{r.failed ? '—' : r.bookings}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {r.failed ? '—' : inr(r.revenue.razorpayGross)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {r.failed ? '—' : inr(r.revenue.cashGross)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>
                      {r.failed ? '—' : inr(r.revenue.tax)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                      {r.failed ? '—' : inr(r.revenue.total)}
                    </TableCell>
                    <TableCell align="right" sx={{ minWidth: 110 }}>
                      {r.failed ? '—' : (
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(100, share)}
                            sx={{ flex: 1, height: 6, borderRadius: 3 }}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ width: 34 }}>
                            {share.toFixed(0)}%
                          </Typography>
                        </Stack>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Open this location">
                        <OpenInNew
                          sx={{ fontSize: 16, cursor: 'pointer', color: 'text.disabled' }}
                          onClick={() => navigate(`/studios/${r.location._id}`)}
                        />
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}

              {!!rows.length && (
                <TableRow sx={{ bgcolor: colors.backgroundTertiary }}>
                  <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{totalBookings}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                    {inr(totals.razorpayGross)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                    {inr(totals.cashGross)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>
                    {inr(totals.tax)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                    {inr(totals.total)}
                  </TableCell>
                  <TableCell /><TableCell />
                </TableRow>
              )}

              {!loading && !rows.length && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                    <Storefront sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                    <Typography color="text.secondary">No locations to report on.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Paper>

      {loading && !rows.length && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
};

export default BrandDashboard;
